import { CurrencyPipe, DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, distinctUntilChanged, Subject, switchMap, takeUntil } from 'rxjs';
import { ConsignmentService } from 'src/app/services/Consignment.service';
import { GeneralService } from 'src/app/services/general.service';
import { PosSettingsService } from 'src/app/services/pos-settings.service';
import { OperationRentalService } from 'src/app/services/Rentals.service';
import { ReportsService } from 'src/app/services/report.service';
import { VendorService } from 'src/app/services/Vendor.service';
import { CurrencyFormatterDirective } from 'src/app/Shared/currencyFormatter.directive';
import { EActionTypes, EToaster, EUserSecurityFunctionID } from 'src/app/Shared/enum';
import { LoaderComponent } from 'src/app/Shared/loader/loader.component';
import { IUserPermissionHandler } from 'src/app/Shared/model/CommonModel';
import { IReportResponse } from 'src/app/Shared/model/ReportsModel';
import { PercentageFormatterDirective } from 'src/app/Shared/percentageFormatter.directive';
import { ANGULAR_MODULES, OperationsComponent, SYNCFUSION_MODULES, TopBarComponent } from 'src/app/Shared/shared-imports';
import { SharedHTMLSettingsDataService } from 'src/app/Shared/sharedHTMLSettingsData.service';
import { UserPermissionHandlerDirective } from 'src/app/Shared/userPermissionHandler.directive';
import { VendorSearchComponent } from 'src/app/Shared/vendor-search/vendor-search.component';
import { WordReplacePipe } from 'src/app/Shared/word-replace.pipe';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-ops-consignment-inventory',
  imports: [SYNCFUSION_MODULES, ANGULAR_MODULES, OperationsComponent, TopBarComponent, WordReplacePipe, VendorSearchComponent,
    UserPermissionHandlerDirective, PercentageFormatterDirective, CurrencyFormatterDirective, LoaderComponent],
  standalone: true,
  templateUrl: './ops-consignment-inventory.html',
  styleUrl: './ops-consignment-inventory.scss',
  providers: [CurrencyPipe, DecimalPipe]
})

export class OpsConsignmentInventory implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();
  loaderIcon: Promise<boolean>;
  isCollapsed = true;
  consignmentForm!: FormGroup;
  barcodeTempForm!: FormGroup;
  searchFieldOptions: { id: number; name: string }[] = [
    { id: 1, name: "Booth Id" },
    { id: 2, name: "Product Name" },
    { id: 3, name: "Search By Date-Active" },
    { id: 4, name: "Stock Id" },
    { id: 5, name: "Vendor Id" },
    { id: 6, name: "Vendor Name" }
  ];
  selectedSearchField: number = 6;
  consignmentInventory: any[] = [];
  searchValue$ = new Subject<string>();
  VendorFilterOption = {
    All: true,
    Current: false,
    Inactive: false,
    PageModel: {
      pageCount: 1,
      pageSize: 20
    }
  };
  isEditMode: boolean = false;
  defaultDealerCommission: number = 0;
  originalRowData: any = null;
  isUpdateEnabled: boolean = false;
  selectedPrintLabels: number[] = [];
  vendorConsignmentCommission: number = 0;
  consignmentBoothIDs: string[] = [];
  viewPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.ConsignmentInventory, Action: EActionTypes.VIEW };
  createPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.ConsignmentInventory, Action: EActionTypes.CREATE };
  deletePermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.ConsignmentInventory, Action: EActionTypes.DELETE };
  editPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.ConsignmentInventory, Action: EActionTypes.EDIT };
  reportUrl;
  marketId: any;
  allowAutoConsignmentId: boolean = false;
  consignmentAutoId: number = 0;
  vendorTerm: string = 'Vendor';
  boothTerm: string = "Booth";
  minExpiryDate: Date = new Date();
  maxExpiryDate: Date = new Date();
  marketCurrentDate!: Date;
  selectAllLabels: boolean = false;

  constructor(
    private fb: FormBuilder,
    private generalService: GeneralService,
    private consignmentService: ConsignmentService,
    private posConsignmentService: PosSettingsService,
    private rentalService: OperationRentalService,
    private vendorService: VendorService,
    private reportService: ReportsService,
    private wordReplacePipe: WordReplacePipe,
    private toastr: ToastrService,
    private sharedHTMLSettings: SharedHTMLSettingsDataService,
    private router: Router,
    private currencyPipe: CurrencyPipe,
    private decimalPipe: DecimalPipe,
  ) { }

  ngOnInit(): void {
    this.initForms();
    this.vendorTerm = this.wordReplacePipe.transform('Vendor');
    this.boothTerm = this.wordReplacePipe.transform('Booth');
    this.getReportUrl();
    this.rentalService.getDefaultMarketId().subscribe((res: any) => {
      if (res) {
        this.marketId = res;
      }
    });
    this.loadMarketCurrentDate();
    this.loadConsignmentSettings();
    this.setupFormSubscriptions();
    this.setupMarketVendorIdListener();
    this.loadConsignmentInventory();
    this.loadConsignmentBoothIDs();
    this.loadConsignmentInventoryBySearchValue();
    this.consignmentForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.checkForChanges());
  }

  initForms(): void {
    this.consignmentForm = this.fb.group({
      ConsSeqProdId: [0],
      MarketId: [0],
      StockId: ['', [Validators.required, Validators.maxLength(15)]],
      ProductName: ['', [Validators.required, Validators.maxLength(50)]],
      ProductDesc: [''],
      VendorId: [null, [Validators.required]],
      MarketVendorId: [null],
      VendorName: ['', [Validators.maxLength(50)]],
      ConsignSpaceId: ['', [Validators.required, Validators.maxLength(10)]],
      Price: [0, [Validators.required, Validators.min(1)]],
      DateAdded: [null],
      DateActive: [null],
      DateRenewal: [null],
      DateExpire: [null],
      SoldOut: [false],
      CommisionPercent: [0],
      CommisionAmount: [0],
      ConsignmentAutoID: [null],
    });

    this.barcodeTempForm = this.fb.group({
      BarcodeIds: ['', Validators.required],
      UsedLabel: [0],
    });
  }

  loadMarketCurrentDate(): void {
    this.generalService.GetMarketCurrentTime().pipe(takeUntil(this.destroy$)).subscribe({
      next: (marketTime: string) => {
        const marketDate = marketTime.split('T')[0];
        this.marketCurrentDate = this.toLocalDate(marketDate);
        this.minExpiryDate = this.marketCurrentDate;
      },
      error: (err) => {
        console.error("Error fetching market time", err);
      }
    });
  }

  get editRight(): boolean {
    return this.generalService.getUserPermissionsForModule(EUserSecurityFunctionID.ConsignmentInventory).EditRight;
  }

  loadConsignmentInventory(): void {
    this.loaderIcon = Promise.resolve(true);
    this.consignmentService.getConsignmentProductByMarket()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.consignmentInventory = data.map(x => ({
            ...x,
            PrintLabel: false
          }));
          this.selectAllLabels = false;
          this.setNextConsignmentAutoId();
          this.loaderIcon = Promise.resolve(false);
        },
        error: (err) => {
          this.loaderIcon = Promise.resolve(false);
          this.toastr.error($localize`Failed to load consignment inventory`, $localize`Error`, this.sharedHTMLSettings.ToasterConfig);
          console.error(err);
        }
      });
  }

  loadConsignmentInventoryBySearchValue(): void {
    this.searchValue$.pipe(debounceTime(500), distinctUntilChanged(),
      switchMap(searchText => {
        if (!searchText || searchText.trim() === '') {
          return this.consignmentService.getConsignmentProductByMarket();
        }
        return this.consignmentService.getConsignmentProductBySearchValue(
          this.selectedSearchField,
          searchText.trim()
        );
      }
      ),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (data: any) => { this.consignmentInventory = data },
      error: (err) => {
        this.toastr.error($localize`Search failed`, $localize`Error`, this.sharedHTMLSettings.ToasterConfig);
        console.error(err);
      }
    });
  }

  loadConsignmentSettings(): void {
    this.posConsignmentService.getSalesTaxAndCommission().pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (settings) => {
          if (settings?.Commission?.DealerConCommPercent != null) {
            this.defaultDealerCommission = settings.Commission.DealerConCommPercent;
          }
        },
        error: (err) => {
          console.error('Failed to load POS settings', err);
        }
      });

    this.posConsignmentService.getPosSettings().pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        this.allowAutoConsignmentId = res?.OtherSettings?.AllowAutoConsignmentId ?? false;
      },
      error: (err) => {
        console.error('Failed to load pos settings', err);
      }
    });
  }

  loadConsignmentBoothIDs(): void {
    this.consignmentService.getConsignmentBoothIDs().subscribe({
      next: (res: string[]) => {
        this.consignmentBoothIDs = res;
        //console.log('Booth IDs:', this.consignmentBoothIDs);
      },
      error: (err) => {
        console.error('Error loading booth IDs', err);
      }
    });
  }

  getReportUrl() {
    this.generalService.getReportUrl().subscribe((response: any) => {
      this.reportUrl = response;
    });
  }

  invokeReport(obj) {
    var openInNewTab = '_blank';
    this.loaderIcon = Promise.resolve(true);
    this.reportService.generateReport(obj).subscribe((response: IReportResponse) => {
      if (response.Success) {
        window.open(this.reportUrl + `report?id=` + response.ReportRequestId,
          openInNewTab, 'width=' + (screen.availWidth - 5) + ',height=' + (screen.availHeight - 5));
        this.loaderIcon = Promise.resolve(false);
      } else {
        console.log('Error on generating Operational Report');
        this.loaderIcon = Promise.resolve(false);
      }
    });
  }

  onReportView() {

    const obj: any = {
      ReportName: "BarcodeAvery5160",
      ReportParameters: {},
      MarketId: this.marketId
    };
    const formValue = this.barcodeTempForm.value;

    obj.ReportParameters["MarketId"] = Number(this.marketId);
    obj.ReportParameters["VendorId"] = Number(0);
    obj.ReportParameters["BarcodeIds"] = formValue.BarcodeIds;
    obj.ReportParameters["UsedLabel"] = Number(formValue.UsedLabel);

    //Template Settings
    obj.ReportParameters["MaxDisplayColumns"] = Number(3);
    obj.ReportParameters["DisplayWithLabels"] = Number(1);
    obj.ReportParameters["DisplayVendorId"] = Number(0);
    obj.ReportParameters["DisplayStockId"] = Number(1);
    obj.ReportParameters["DisplayItemName"] = Number(1);
    obj.ReportParameters["DisplayPrice"] = Number(1);

    obj.ReportParameters["IsConsignmentInventory"] = Number(1);

    console.log("Barcode report parameters", obj.ReportParameters);

    this.invokeReport(obj);
  }

  onSearchChange(value: string): void {
    this.searchValue$.next(value?.trim() || '');
  }

  onDateChange(event: any): void {
    if (event?.value) {
      const d: Date = new Date(event.value);
      //console.log("date change", d)
      const formattedDate =
        d.getFullYear() +
        "-" +
        String(d.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(d.getDate()).padStart(2, "0");
      this.searchValue$.next(formattedDate);
    } else {
      this.loadConsignmentInventory();
    }
  }

  openConsignmentModal(isEdit: boolean = false) {
    if (!isEdit) {
      const today = new Date();

      const expiryDate = new Date(today);
      const renewalDate = new Date(today);
      expiryDate.setMonth(today.getMonth() + 6);
      renewalDate.setMonth(today.getMonth() + 3);

      const formatDateMMDDYYYY = (date: Date) =>
        `${(date.getMonth() + 1).toString().padStart(2, '0')}-` +
        `${date.getDate().toString().padStart(2, '0')}-` +
        `${date.getFullYear()}`;

      this.consignmentForm.reset({
        ConsSeqProdId: 0,
        MarketId: 0,
        StockId: this.allowAutoConsignmentId ? `${this.consignmentAutoId}` : '',
        ProductName: '',
        ProductDesc: '',
        VendorId: null,
        MareketVendorId: null,
        VendorName: '',
        ConsignSpaceId: '',
        Price: 0,
        DateAdded: new Date(today),
        DateActive: new Date(today),
        DateRenewal: new Date(renewalDate),
        DateExpire: new Date(expiryDate),
        SoldOut: false,
        CommisionPercent: this.defaultDealerCommission,
        CommisionAmount: 0,
        ConsignmentAutoID: this.allowAutoConsignmentId ? this.consignmentAutoId : Number(0)
      });

      //ConsignmentAutoID logic
      const autoIdControl = this.consignmentForm.get('StockId');

      if (this.allowAutoConsignmentId) {
        autoIdControl?.setValue(String(this.consignmentAutoId), { emitEvent: false });
        autoIdControl?.disable({ emitEvent: false });
      } else {
        autoIdControl?.enable({ emitEvent: false });
      }

      this.formatInitialValues();
      this.isEditMode = false;
    }

    this.consignmentForm.get('DateAdded')?.disable({ emitEvent: false });
    this.openModalById('consignmentInventoryModal')
  }

  closeConsignmentModal() {
    const modalEl = document.getElementById('consignmentInventoryModal');
    if (modalEl) {
      const modalInstance = window['bootstrap'].Modal.getInstance(modalEl);
      if (modalInstance) {
        modalInstance.hide();
      }
    }
  }

  openVendorSearchModal() {
    const modalEl = document.getElementById('vendorSearchModal');
    if (modalEl) {
      let vendorModal = window['bootstrap'].Modal.getInstance(modalEl);
      if (!vendorModal) {
        vendorModal = new window['bootstrap'].Modal(modalEl, {
          backdrop: false,
          keyboard: false
        });
      }
      vendorModal.show();
    }
  }

  onRowSelected(args: any) {

    const row = args?.rowData;
    if (!row) return;

    if (args.column?.field === 'printLabel') {
      return;
    }

    const selectedRow = args?.rowData;

    if (!this.editRight) {
      this.toastr.info(
        EToaster.MsgInfoNoRightToEdit + $localize`:@@Consignment Inventory:Consignment Inventory`,
        EToaster.TitleInfo,
        this.sharedHTMLSettings.ToasterConfig
      );
      return;
    }

    if (selectedRow.VendorId) {
      this.vendorService.GetVendorById(selectedRow.VendorId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (vendorData) => {
            if (vendorData?.MarketVendorId) {
              this.consignmentForm.patchValue(
                { MarketVendorId: vendorData.MarketVendorId },
                { emitEvent: false }
              );
            }
          },
          error: (err) => {
            console.error('Error fetching vendor info by ID', err);
          }
        });
    }

    const fullStockId = selectedRow.StockId || '';
    const cstockId = fullStockId.replace(/^CON/i, ''); // remove CON

    this.originalRowData = {
      ...selectedRow,
      StockId: (cstockId || '').trim(),
      ProductName: (selectedRow.ProductName || '').trim(),
    };

    this.consignmentForm.patchValue({
      ConsSeqProdId: selectedRow.ConsSeqProdId || 0,
      MarketId: selectedRow.MarketId || 0,
      StockId: cstockId || '',
      ProductName: selectedRow.ProductName || '',
      ProductDesc: selectedRow.ProductDesc || '',
      VendorId: selectedRow.VendorId || null,
      VendorName: selectedRow.VendorName || '',
      ConsignSpaceId: selectedRow.ConsignSpaceId || '',
      Price: selectedRow.Price || 0,
      DateAdded: selectedRow.DateAdded ? new Date(selectedRow.DateAdded) : null,
      DateActive: selectedRow.DateActive ? new Date(selectedRow.DateActive) : null,
      DateRenewal: selectedRow.DateRenewal ? new Date(selectedRow.DateRenewal) : null,
      DateExpire: selectedRow.DateExpire ? new Date(selectedRow.DateExpire) : null,
      SoldOut: selectedRow.SoldOut ?? false,
      CommisionPercent: selectedRow.CommisionPercent || 0,
      CommisionAmount: selectedRow.CommisionAmount || 0,
      ConsignmentAutoID: selectedRow.ConsignmentAutoID || null,
    });

    this.isEditMode = true;
    this.isUpdateEnabled = false;
    this.formatInitialValues();
    this.openConsignmentModal(true);

  }

  checkForChanges() {
    if (!this.originalRowData) return;

    const formValue = this.consignmentForm.value;

    const stockIdChanged = (formValue.StockId || '').trim() !== this.originalRowData.StockId;
    const productNameChanged = (formValue.ProductName || '').trim() !== this.originalRowData.ProductName;

    const vendorChanged = formValue.VendorId !== this.originalRowData.VendorId;
    const priceChanged = parseFloat(formValue.Price) !== parseFloat(this.originalRowData.Price);
    const commPercentChanged = parseFloat(formValue.CommisionPercent) !== parseFloat(this.originalRowData.CommisionPercent);
    const consSpaceChanged = formValue.ConsignSpaceId !== this.originalRowData.ConsignSpaceId;

    const dateFields: Array<keyof typeof formValue> = ['DateAdded', 'DateActive', 'DateExpire', 'DateRenewal'];
    const dateChanged = dateFields.some(field => {
      const formDate = formValue[field] ? new Date(formValue[field]).getTime() : null;
      const originalDate = this.originalRowData[field] ? new Date(this.originalRowData[field]).getTime() : null;
      return formDate !== originalDate;
    });

    this.isUpdateEnabled = stockIdChanged || productNameChanged || vendorChanged || priceChanged || commPercentChanged || consSpaceChanged || dateChanged;
  }

  showValidationErrors(form: FormGroup): boolean {
    form.markAllAsTouched();

    const missingFields: string[] = [];

    if (form.get('StockId')?.hasError('required')) missingFields.push('Stock ID');
    if (form.get('ProductName')?.hasError('required')) missingFields.push('Product Name');
    if (form.get('VendorId')?.hasError('required')) missingFields.push(`${this.vendorTerm} ID`);
    if (form.get('ConsignSpaceId')?.hasError('required')) missingFields.push(`Consign ${this.boothTerm} ID`);

    let priceError: string | null = null;
    if (form.get('Price')?.hasError('required')) {
      priceError = 'Price is required';
    } else if (form.get('Price')?.hasError('min')) {
      priceError = 'Price must be greater than 0';
    }

    const totalErrors = missingFields.length + (priceError ? 1 : 0);

    if (totalErrors === 1 && priceError) {
      this.toastr.warning(priceError, 'Validation', this.sharedHTMLSettings.ToasterConfig);
      return true;
    } else if (totalErrors === 1) {
      this.toastr.warning(
        `Please fill: ${missingFields[0]}`,
        'Validation',
        this.sharedHTMLSettings.ToasterConfig
      );
      return true;
    } else if (totalErrors > 1 && totalErrors <= 3) {
      this.toastr.warning(
        `Please fill: ${missingFields.join(', ')}`,
        'Validation',
        this.sharedHTMLSettings.ToasterConfig
      );
      return true;
    } else if (totalErrors > 3) {
      this.toastr.warning(
        'Please fill all required fields',
        'Validation',
        this.sharedHTMLSettings.ToasterConfig
      );
      return true;
    }
    return false;
  }

  saveConsignmentProduct(): void {
    if (this.showValidationErrors(this.consignmentForm)) {
      console.log("error in while saving")
      return;
    }

    const formatDateForApi = (date: any) => {
      if (!date) return null;
      const d = new Date(date);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    Swal.fire({
      title: this.isEditMode ? 'Update Product?' : 'Add Product?',
      text: this.isEditMode
        ? 'Do you want to update this consignment product?'
        : 'Do you want to add this consignment product?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: this.isEditMode ? 'Update' : 'Add',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        const raw = this.consignmentForm.getRawValue();
        raw.StockId = raw.StockId ? String(raw.StockId).trim() : '';
        raw.ProductName = raw.ProductName ? String(raw.ProductName).trim() : '';
        const payload = {
          ...raw,
          DateAdded: formatDateForApi(raw.DateAdded),
          DateActive: formatDateForApi(raw.DateActive),
          DateExpire: formatDateForApi(raw.DateExpire),
          DateRenewal: formatDateForApi(raw.DateRenewal)
        };
        this.consignmentService.saveConsignmentProductByMarket(payload)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res: any) => {
              if (res.Success) {
                const msg = this.isEditMode
                  ? 'Consignment product updated successfully'
                  : 'Consignment product added successfully';
                this.toastr.success(res.Message || msg, 'Success', this.sharedHTMLSettings.ToasterConfig);
                this.reloadInventoryWithCurrentSearch();
                this.selectAllLabels = false;
                this.closeConsignmentModal();
              } else {
                this.toastr.error(res.Message || 'Save failed', 'Error', this.sharedHTMLSettings.ToasterConfig);
              }
            },
            error: (err) => {
              console.error(err);
              this.toastr.error('Error occurred while saving', 'Error', this.sharedHTMLSettings.ToasterConfig);
            }
          });
      }
    });
  }

  deleteConsignmentProduct(product: any): void {
    Swal.fire({
      title: 'Delete Product?',
      text: `Do you want to delete Stock Id "${product.StockId}" with Product Name "${product.ProductName}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.consignmentService.deleteConsignmentProductById(product.ConsSeqProdId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res: any) => {
              if (res.Success) {
                this.toastr.success('Consignment Product deleted successfully', 'Success', this.sharedHTMLSettings.ToasterConfig);
                this.reloadInventoryWithCurrentSearch();
              } else {
                this.toastr.error(res.Message || 'Failed to delete consignment product', 'Error', this.sharedHTMLSettings.ToasterConfig);
              }
            },
            error: (err) => {
              console.error(err);
              this.toastr.error('Error occurred while deleting product', 'Error', this.sharedHTMLSettings.ToasterConfig);
            }
          });
      }
    });
  }

  reloadInventoryWithCurrentSearch(): void {
    let searchValue: string | null = null;
    if (this.selectedSearchField === 3) {
      const datePicker = document.getElementById('txtSearchDate') as any;
      if (datePicker?.ej2_instances?.[0]?.value) {
        const d: Date = new Date(datePicker.ej2_instances[0].value);
        searchValue = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      }
    } else {
      const inputEl = document.getElementById('txtSearchValue') as HTMLInputElement;
      if (inputEl) {
        searchValue = inputEl.value.trim();
      }
    }
    if (searchValue) {
      this.consignmentService.getConsignmentProductBySearchValue(this.selectedSearchField, searchValue)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (data: any) => this.consignmentInventory = data,
          error: (err) => {
            this.toastr.error('Search failed', 'Error', this.sharedHTMLSettings.ToasterConfig);
            console.error(err);
          }
        });
    } else {
      this.loadConsignmentInventory();
    }
  }

  onVendorSelected(event: any) {
    if (event) {
      this.consignmentForm.patchValue({
        VendorId: event.vendorId,
        VendorName: event.vendorName,
        MarketVendorId: event.marketVendorId
      });
      this.fetchAndSetVendorCommission(event.vendorId);
    }

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }

  fetchAndSetVendorCommission(id: number): void {
    this.vendorService.GetMarketVendorSettings(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res) {
            this.vendorConsignmentCommission = res.ConsignmentCommission ?? 0;
            const formattedCommission = Number(this.vendorConsignmentCommission);
            this.consignmentForm.patchValue({
              CommisionPercent: formattedCommission > 0 ? formattedCommission : this.defaultDealerCommission
            });
            this.formatInitialValues();
          }
        },
        error: (err) => {
          console.error('Error fetching vendor settings', err);
          this.vendorConsignmentCommission = 0;
          this.consignmentForm.patchValue({ CommisionPercent: this.defaultDealerCommission });
        }
      });
  }

  setupMarketVendorIdListener(): void {
    this.consignmentForm.get('MarketVendorId')?.valueChanges.pipe(
      debounceTime(1000),
      distinctUntilChanged(),
      switchMap((id: string) => {
        if (!id) {
          this.consignmentForm.patchValue(
            {
              VendorId: '',
              VendorName: '',
              CommisionPercent: this.defaultDealerCommission
            },
            { emitEvent: false }
          );
          this.formatInitialValues();
          return [];
        }

        return this.vendorService.getVendorInfoByMarketVendorId(id);
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (res: any) => {

        if (res?.MarketVendorId == null) {
          Swal.fire({
            icon: 'warning',
            title: 'Vendor Not Found',
            text: 'No vendor found for the entered Vendor ID.',
            confirmButtonText: 'OK'
          });

          this.consignmentForm.patchValue({
            MarketVendorId: '',
            VendorId: '',
            VendorName: '',
            CommisionPercent: this.defaultDealerCommission
          }, { emitEvent: false });

          this.formatInitialValues();
          return;
        }

        if (res.IsInactive) {

          Swal.fire({
            icon: 'warning',
            title: 'Inactive Vendor',
            text: `${res.VendorName} (Vendor ID: ${res.MarketVendorId}) is inactive and cannot be selected.`,
            confirmButtonText: 'OK'
          });

          this.consignmentForm.patchValue({
            MarketVendorId: '',
            VendorId: '',
            VendorName: '',
            CommisionPercent: this.defaultDealerCommission
          }, { emitEvent: false });

          this.formatInitialValues();
          return;
        }

        this.consignmentForm.patchValue({
          VendorId: res.VendorId,
          VendorName: res.VendorName
        }, { emitEvent: false });

        this.fetchAndSetVendorCommission(res.VendorId);
      },
      error: () => {
        this.consignmentForm.patchValue(
          {
            VendorId: '',
            VendorName: '',
            CommisionPercent: this.defaultDealerCommission
          },
          { emitEvent: false }
        );
      }
    });
  }

  formatInitialValues() {
    const form = this.consignmentForm;

    const price = form.get('Price')?.value || 0;
    const commissionPercent = form.get('CommisionPercent')?.value || 0;
    const commissionAmount = form.get('CommisionAmount')?.value || 0;

    const priceInput = document.querySelector<HTMLInputElement>('input[formControlName="Price"]');
    const percentInput = document.querySelector<HTMLInputElement>('input[formControlName="CommisionPercent"]');
    const amountInput = document.querySelector<HTMLInputElement>('input[formControlName="CommisionAmount"]');

    if (priceInput) priceInput.value = this.currencyPipe.transform(price, '$', 'symbol', '1.2-2') || '$0.00';
    if (percentInput) percentInput.value = this.decimalPipe.transform(commissionPercent, '1.2-4') + '%';
    if (amountInput) amountInput.value = this.currencyPipe.transform(commissionAmount, '$', 'symbol', '1.2-2') || '$0.00';
  }

  updateCommissionAmount() {
    const form = this.consignmentForm;
    let price = parseFloat(form.get('Price')?.value) || 0;
    let percent = parseFloat(form.get('CommisionPercent')?.value) || 0;
    const priceParts = price.toString().split('.');
    if (priceParts[0].length > 7) {
      const limitedInteger = priceParts[0].slice(0, 7);
      const decimalPart = priceParts[1] ? '.' + priceParts[1] : '';
      price = parseFloat(limitedInteger + decimalPart);
      form.get('Price')?.setValue(price, { emitEvent: false });
    }
    let commissionAmount = (price * percent) / 100;
    form.get('CommisionAmount')?.setValue(commissionAmount, { emitEvent: false });
    const amountInput = document.querySelector<HTMLInputElement>(
      'input[formControlName="CommisionAmount"]'
    );
    if (amountInput) {
      amountInput.value =
        this.currencyPipe.transform(commissionAmount, '$', 'symbol', '1.2-2') || '$0.00';
    }
  }

  setupFormSubscriptions(): void {
    const form = this.consignmentForm;
    const controlsToWatch = ['Price', 'CommisionPercent'];
    controlsToWatch.forEach(controlName => {
      form.get(controlName)?.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.updateCommissionAmount());
    });
    setTimeout(() => this.formatInitialValues(), 0);
  }

  onSearchFieldChange(): void {
    this.resetSearchValue();
  }

  resetSearchValue(): void {
    const inputEl = document.getElementById('txtSearchValue') as HTMLInputElement;
    if (inputEl) {
      inputEl.value = '';
    }

    const datePicker = document.getElementById('txtSearchDate') as any;
    if (datePicker?.ej2_instances?.[0]) {
      datePicker.ej2_instances[0].value = null;
      datePicker.ej2_instances[0].dataBind();
    }
  }

  onPrintLabelChange(row: any, event: any): void {
    const checked = event.target.checked;
    const barcodeId = Number(row.ConsSeqProdId);

    const actualRow = this.consignmentInventory.find(x => x.ConsSeqProdId === barcodeId);
    if (actualRow) {
      actualRow.PrintLabel = checked;
    }

    if (checked) {
      if (!this.selectedPrintLabels.includes(barcodeId)) {
        this.selectedPrintLabels.push(barcodeId);
      }
    } else {
      this.selectedPrintLabels = this.selectedPrintLabels.filter(
        id => id !== barcodeId
      );
    }

    this.selectAllLabels =
      this.consignmentInventory.length > 0 &&
      this.consignmentInventory.every(x => x.PrintLabel);

    this.updateBarcodeIds();
  }

  updateBarcodeIds(): void {
    const sortedIds = [...this.selectedPrintLabels].sort((a, b) => a - b);

    const barcodeString = sortedIds.join(',');

    this.barcodeTempForm.patchValue({
      BarcodeIds: barcodeString
    });

    //console.log('BarcodeIds:', this.barcodeTempForm.get('BarcodeIds')?.value);
  }

  get isPrintEnabled(): boolean {
    const value = this.barcodeTempForm.get('BarcodeIds')?.value;
    return value && value.length > 0;
  }

  getNextConsignmentAutoId(data: any[]): number {
    if (!data || data.length === 0) {
      return 1; // default if no data
    }
    const maxId = Math.max(
      ...data.map(item => item.ConsignmentAutoID || 0)
    );
    return maxId + 1;
  }

  setNextConsignmentAutoId(): void {
    this.consignmentService.getNextConsignmentAutoId()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: number) => {
          this.consignmentAutoId = (res || 0) + 1;
        },
        error: (err) => {
          console.error(err);
        }
      });
  }

  onDateChangeCheck(controlName: 'DateActive' | 'DateExpire', event: any): void {

    const newValue = event?.value;

    const activeVal = controlName === 'DateActive'
      ? newValue
      : this.consignmentForm.get('DateActive')?.value;

    const expiryVal = controlName === 'DateExpire'
      ? newValue
      : this.consignmentForm.get('DateExpire')?.value;

    if (!activeVal || !expiryVal) return;

    const normalize = (d: Date) =>
      new Date(d.getFullYear(), d.getMonth(), d.getDate());

    const activeDate = normalize(activeVal);
    const expiryDate = normalize(expiryVal);

    //console.log("Active:", activeDate, "Expiry:", expiryDate);

    if (expiryDate < activeDate) {
      Swal.fire({
        icon: 'warning',
        text: 'Expiry Date should be greater than Active Date!',
        confirmButtonText: 'OK'
      });
      if (this.isEditMode && this.originalRowData) {

        this.consignmentForm.patchValue({
          DateActive: controlName === 'DateActive'
            ? this.originalRowData.DateActive
            : activeVal,

          DateExpire: controlName === 'DateExpire'
            ? this.originalRowData.DateExpire
            : expiryVal

        }, { emitEvent: false });

      } else {
        const today = new Date();

        const expiryDefault = new Date(today);
        expiryDefault.setMonth(today.getMonth() + 6);

        if (controlName === 'DateActive') {
          this.consignmentForm.get('DateActive')
            ?.setValue(today, { emitEvent: false });

        } else if (controlName === 'DateExpire') {
          this.consignmentForm.get('DateExpire')
            ?.setValue(expiryDefault, { emitEvent: false });
        }
      }
    }
  }

  closeModalById(modalId: string) {
    const modalEl = document.getElementById(modalId);
    if (modalEl) {
      const modalInstance = window['bootstrap'].Modal.getInstance(modalEl);
      if (modalInstance) {
        modalInstance.hide();
      }
    }

    const backdrops = document.getElementsByClassName('modal-backdrop');
    while (backdrops.length > 0) {
      backdrops[0].parentNode?.removeChild(backdrops[0]);
    }

    document.body.classList.remove('modal-open');

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }

  openModalById(modalId: string) {
    const modalEl = document.getElementById(modalId);
    if (modalEl) {
      let modal = window['bootstrap'].Modal.getInstance(modalEl);
      if (!modal) {
        modal = new window['bootstrap'].Modal(modalEl, { backdrop: 'static', keyboard: false });
      }
      modal.show();
    }
  }

  percentFormatter(field: string, data: any): string {
    return data[field] != null ? data[field].toFixed(2) + ' %' : '';
  }

  allowOnlyAlphanumeric(event: KeyboardEvent) {
    const allowedKeys = [
      'Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Enter'
    ];
    if (allowedKeys.includes(event.key)) {
      return;
    }
    const regex = /^[a-zA-Z0-9-]$/;
    if (!regex.test(event.key)) {
      event.preventDefault();
      return;
    }
  }

  allowOnlyDigits(event: KeyboardEvent, maxDigits: number) {
    const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Enter'];

    if (allowedKeys.includes(event.key)) {
      return;
    }
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
      return;
    }
    // Limit max digits
    const input = event.target as HTMLInputElement;
    if (input.value.length >= maxDigits) {
      event.preventDefault();
    }
  }

  onUsedLabelBlur() {
    const control = this.barcodeTempForm.get('UsedLabel');
    if (!control) return;
    control.setValue(Number(control.value || 0));
  }

  toLocalDate(dateString: string): Date {
    const [y, M, d] = dateString.split('-').map(Number);
    return new Date(y, M - 1, d);
  }

  onSelectAllLabels(): void {

    const checked = this.selectAllLabels;

    this.consignmentInventory.forEach(row => {
      row.PrintLabel = checked;
    });

    if (checked) {
      this.selectedPrintLabels = this.consignmentInventory
        .map(row => Number(row.ConsSeqProdId))
        .filter(id => id > 0);
    } else {
      this.selectedPrintLabels = [];
    }

    this.updateBarcodeIds();

    this.consignmentInventory = [...this.consignmentInventory];

  }

  onCancel(): void {
    this.isCollapsed = true;
    this.router.navigate(['/dashboard']);
  }

  onBeforeOpen(args: any): void {
    if (!args || !args.target || !args.target.dataset || !args.target.dataset.content) {
      args.cancel = true;
    }
  }

  handleToggle(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
